import React, { useMemo } from 'react';
import Autocomplete, {
  type AutocompleteChangeReason,
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type { SxProps, Theme } from '@mui/material/styles';
import type { FilterOption } from '../../types/common';
import { useRegisterFilterActivity } from '../FilterDrawer/FilterActivityContext';

interface SingleSelectFilterProps {
  label?: string;
  placeholder?: string;
  options: Array<FilterOption>;
  /** Selected option id, or null when nothing is selected. */
  value: string | number | null;
  onChange: (selected: string | number | null) => void;
  /**
   * When true the selection cannot be cleared (no X icon). Use for required
   * filters like Age Group; leave false for optional ones like Conference
   * (clearing returns to the "All" state, i.e. `null`).
   */
  disableClearable?: boolean;
  sx?: SxProps<Theme>;
  testId?: string;
  /**
   * When provided, this filter self-reports its active state to the
   * surrounding `FilterDrawerContainer`'s activity registry. Defaults to
   * "active when a value is selected"; pass `isActive` to override (e.g. a
   * required filter is only "active" when it differs from its default).
   */
  filterId?: string;
  isActive?: boolean;
  /** Fired when the user picks an option (not on clear). */
  onApply?: (selected: FilterOption | null) => void;
  /** Fired when the user clicks the X (clear) icon. */
  onClear?: () => void;
}

/**
 * Reusable single-select dropdown built on MUI Autocomplete — the single-value
 * counterpart to `MultiSelectFilter`, using the same `FilterOption {id, name}`
 * shape and `FilterActivityContext` registration.
 */
const SingleSelectFilter: React.FC<SingleSelectFilterProps> = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  disableClearable = false,
  sx,
  testId,
  filterId,
  isActive,
  onApply,
  onClear,
}) => {
  useRegisterFilterActivity(filterId, isActive ?? value !== null);

  const selectedOption = useMemo(
    () => options.find((option) => option.id === value) ?? null,
    [options, value]
  );

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={selectedOption}
      disableClearable={disableClearable}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(
        _,
        newValue: FilterOption | null,
        reason: AutocompleteChangeReason
      ) => {
        onChange(newValue ? newValue.id : null);
        if (reason === 'clear') {
          onClear?.();
        } else if (reason === 'selectOption') {
          onApply?.(newValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value === null ? placeholder : ''}
        />
      )}
      sx={sx}
      data-testid={testId}
    />
  );
};

export default SingleSelectFilter;
